import { memo, useCallback, useEffect, useMemo, useState, type CSSProperties } from 'react'
import { ModalShell } from '../common/ModalShell.js'
import { playoutApi, type GapFillOptions, type LiveBreakRequest, type LiveFillEntry } from '../../lib/api.js'
import { getServerNowMs } from '../../hooks/useServerClock.js'
import type { ScheduleItem } from '../../types/index.js'
import { scheduleItemTitle } from '../../lib/utils.js'
import {
  LiveFillCatalog,
  LiveFillSelection,
  fmtPreset,
  fmtSec,
  useLiveFillSelection,
  type FillItem,
} from './LiveFillComposer.js'
import { resolveLiveBreakBounds } from './liveBreakGeometry.js'
import '../agenda/AgendaEditors.css'
import './LiveSkipDialog.css'

/**
 * Décrochage d'un bloc de direct, en cours d'antenne.
 *
 * Le direct est un bloc unique : l'opérateur décide quand le quitter et avec
 * quoi. La fenêtre creusée peut avoir une durée annoncée — le direct reprend
 * alors tout seul, et « Reprendre le direct » permet d'écourter — ou rester
 * ouverte, et c'est la reprise qui décidera du retour.
 *
 * Les durées affichées sont calculées localement pour rester vivantes pendant
 * que l'opérateur compose ; le serveur reste seul juge au moment de valider
 * (`live-break-plan.ts`), et son refus s'affiche ici sans rien casser à
 * l'antenne.
 */

/** Miroir de `LIVE_SKIP_DEFAULT_LEAD_SEC` : marge de préchargement du comblage. */
const LIVE_BREAK_LEAD_SEC = 10

/** Miroir de `LIVE_BREAK_MIN_RESUME_SEC` côté serveur. */
const MIN_RESUME_SEC = 10

const WINDOW_PRESETS_SEC = [60, 120, 300, 600]

interface LiveBreakDialogProps {
  channelId: string
  liveItem:  ScheduleItem
  /** Premier élément posé derrière : il borne la fenêtre comblable. */
  nextItem?: ScheduleItem | null
  /** Comblage pré-rempli, par exemple depuis le décrochage préfait du jour. */
  initialItems?: FillItem[]
  initialMode?:  'FIXED' | 'OPEN'
  initialWindowSec?: number | null
  onConfirm: (body: Omit<LiveBreakRequest, 'itemId'>) => void | Promise<void>
  onCancel:  () => void
}

function fmtTime(value: string | number) {
  return new Date(value).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', second: '2-digit' })
}

export const LiveBreakDialog = memo(function LiveBreakDialog({
  channelId, liveItem, nextItem = null, initialItems = [], initialMode = 'FIXED', initialWindowSec = null,
  onConfirm, onCancel,
}: LiveBreakDialogProps) {
  const [options, setOptions] = useState<GapFillOptions | null>(null)
  const [loading, setLoading] = useState(false)
  const [saving,  setSaving]  = useState(false)
  const [error,   setError]   = useState('')
  const [now,     setNow]     = useState(getServerNowMs)
  const [mode,    setMode]    = useState<'FIXED' | 'OPEN'>(initialMode)
  const [resume,  setResume]  = useState(true)
  const [windowSec, setWindowSec] = useState<number>(initialWindowSec ?? 120)

  const fill = useLiveFillSelection(options, initialItems)
  // Déstructuré : dépendre de `fill.xxx` obligerait les callbacks ci-dessous à
  // prendre l'objet entier en dépendance, donc à se recréer à chaque frappe.
  const { addInfoserviceRest, removeItem, selected: fillSelected } = fill

  /**
   * « Combler tout le reste » n'a de sens que sans durée annoncée.
   *
   * En mode FIXED, `planLiveBreak` dimensionne l'élastique sur la durée saisie
   * (`totalMs = requestedWindowSec`, `lib/live-break-plan.ts`) : l'infoservice
   * comble alors le reste de **cette durée**, pas le reste jusqu'au prochain
   * élément posé. L'opérateur lisait « comble le reste » et obtenait la valeur
   * du champ secondes. Le geste bascule donc la fenêtre en OPEN, seul mode où
   * le serveur étire l'élastique sur toute la place disponible.
   */
  const handleAddInfoserviceRest = useCallback(() => {
    addInfoserviceRest()
    setMode('OPEN')
  }, [addInfoserviceRest])

  /** Retirer l'élastique rend la main aux durées fixes : OPEN n'a plus de porteur. */
  const handleRemoveItem = useCallback((uid: string) => {
    if (fillSelected.find(item => item.uid === uid)?.fillRest) setMode('FIXED')
    removeItem(uid)
  }, [removeItem, fillSelected])

  useEffect(() => {
    const timer = setInterval(() => setNow(getServerNowMs()), 1000)
    return () => clearInterval(timer)
  }, [])

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    playoutApi.getGapFillOptions(channelId)
      .then(res => { if (!cancelled) setOptions(res) })
      .catch(() => { if (!cancelled) setError('Impossible de charger les contenus disponibles') })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [channelId])

  // ── Géométrie du bloc (miroir du calcul serveur) ────────────────────────────
  //
  // Un direct en fin manuelle qui déborde tient toujours l'antenne : sa fenêtre
  // court alors jusqu'à la prochaine ancre, pas jusqu'à une heure de fin déjà
  // passée (`liveBreakGeometry.ts`).
  const breakAtMs = now + LIVE_BREAK_LEAD_SEC * 1000
  const { blockEndMs, windowEndMs, overrun } = resolveLiveBreakBounds({
    breakAtMs,
    scheduledEndMs:     new Date(liveItem.endTime).getTime(),
    manualEnd:          liveItem.liveEndMode === 'MANUAL',
    nextPendingStartMs: nextItem ? new Date(nextItem.startTime).getTime() : null,
    resume,
  })
  const availableSec = Math.max(0, Math.floor((windowEndMs - breakAtMs) / 1000))

  const effectiveWindowSec = mode === 'OPEN' ? availableSec : windowSec
  const resumeSec  = Math.max(0, availableSec - effectiveWindowSec)

  const posesResume = resume && mode === 'FIXED'

  // Jingle de retour au direct (réglage de la chaîne). Miroir de `planLiveBreak` :
  // posé dans les dernières secondes de la durée annoncée, seulement devant une
  // reprise posée d'avance, et abandonné — pas refusé — faute de place.
  const resumeJingle    = options?.resumeJingle ?? null

  // Jingle de décrochage : le même, en tête du comblage, sauf quand celui-ci
  // commence déjà par un jingle. Miroir de `planLiveBreak` : il passe avant le
  // jingle de retour, et faute de place le décrochage part sans lui.
  const openingDueSec     = resumeJingle && fill.selected[0]?.kind !== 'JINGLE' ? Math.ceil(resumeJingle.durationSec) : 0
  const openingFits       = openingDueSec > 0 && Math.ceil(fill.fixedSec) + openingDueSec <= effectiveWindowSec
  const openingSkipped    = openingDueSec > 0 && !openingFits
  const openingSec        = openingFits ? openingDueSec : 0

  const resumeJingleSec = posesResume && resumeJingle && fill.selected[fill.selected.length - 1]?.kind !== 'JINGLE'
    ? Math.ceil(resumeJingle.durationSec)
    : 0
  const jingleFits      = resumeJingleSec > 0 && Math.ceil(fill.fixedSec) + openingSec + resumeJingleSec <= effectiveWindowSec
  const jingleSkipped   = resumeJingleSec > 0 && !jingleFits
  const jingleSec       = jingleFits ? resumeJingleSec : 0

  const elasticSec = Math.max(0, effectiveWindowSec - fill.fixedSec - openingSec - jingleSec)
  const filledSec  = fill.hasElastic ? effectiveWindowSec : fill.fixedSec + openingSec + jingleSec
  const overfill    = filledSec > availableSec

  // Part de la fenêtre déjà occupée par le contenu composé.
  const progressPct = Math.min(100, Math.round((filledSec / (effectiveWindowSec || 1)) * 100))
  const progressState = overfill ? 'danger' : filledSec >= effectiveWindowSec ? 'success' : 'info'
  const blockReason = useMemo(() => {
    if (availableSec <= 0) {
      return overrun
        ? 'Plus de place avant le programme suivant : terminez le direct'
        : 'Le bloc de direct est déjà terminé'
    }
    if (mode === 'OPEN' && !fill.hasElastic) {
      return 'Un décrochage sans durée fixe exige l’infoservice « combler tout le reste »'
    }
    if (overfill) return 'Le comblage dépasse la place disponible avant la fin du bloc'
    if (effectiveWindowSec < 5) return 'Un décrochage doit durer au moins 5 s'
    if (posesResume && resumeSec < MIN_RESUME_SEC) {
      return `Il ne resterait que ${fmtSec(resumeSec)} de direct : décochez la reprise pour terminer le bloc`
    }
    return ''
  }, [availableSec, overrun, mode, fill.hasElastic, overfill, effectiveWindowSec, posesResume, resumeSec])

  const handleConfirm = useCallback(async () => {
    setSaving(true); setError('')
    try {
      await onConfirm({
        mode,
        resume,
        items: fill.entries as LiveFillEntry[],
        ...(mode === 'FIXED' ? { windowSec } : {}),
      })
    } catch (e: any) {
      setError(e?.message ?? 'Décrochage refusé')
      setSaving(false)
    }
  }, [onConfirm, mode, resume, fill.entries, windowSec])

  const editLocked = saving

  return (
    <ModalShell
      className="agenda-editor gap-fill live-skip"
      closeDisabled={saving}
      dismissOnOverlay={false}
      onClose={onCancel}
      showCloseButton={false}
      size="wide"
      title="Décrocher — le direct reprend derrière"
    >
      <div className="gap-fill__body">
        <div className="gap-fill__summary">
          <div>
            <div className="live-skip__live-indicator">DIRECT EN COURS</div>
            <div className="gap-fill__summary-copy">
              {overrun ? (
                <>
                  Bloc dépassé depuis <strong>{fmtTime(liveItem.endTime)}</strong>
                  {' · '}antenne tenue jusqu'à <strong>{fmtTime(blockEndMs)}</strong>
                  {' · '}reste <strong>{fmtSec(availableSec)}</strong> à combler
                </>
              ) : (
                <>
                  Bloc jusqu'à <strong>{fmtTime(liveItem.endTime)}</strong>
                  {' · '}reste <strong>{fmtSec(availableSec)}</strong> de direct disponible
                </>
              )}
            </div>
          </div>
          {/* Le grand chiffre est la durée du décrochage — la décision en cours.
              Ce qui est composé dedans se lit sur la barre juste dessous. */}
          <div className={`gap-fill__status is-${overfill ? 'danger' : 'info'}`} aria-live="polite">
            <div className="gap-fill__status-value">
              {mode === 'OPEN' ? 'Libre' : fmtSec(effectiveWindowSec)}
            </div>
            <div className="gap-fill__status-detail">
              {posesResume ? `puis direct ${fmtSec(resumeSec)}` : 'le direct ne reprend pas'}
            </div>
          </div>
        </div>

        {/* Remplissage de la fenêtre : vide, complet, ou dépassement. */}
        <div className="gap-fill__progress" data-state={progressState}>
          <div
            className="gap-fill__progress-value"
            style={{ '--gap-fill-progress': `${progressPct}%` } as CSSProperties}
          />
        </div>

        <div className="gap-fill__layout live-skip__layout">
          <div className="gap-fill__selection-column">
            <div className="gap-fill__preview">
              <div className="agenda-editor__eyebrow">Aperçu du décrochage</div>

              <div className="gap-fill__boundary gap-fill__boundary--live">
                <span className="gap-fill__boundary-role">Direct</span>
                <span className="gap-fill__boundary-label">{scheduleItemTitle(liveItem, 'Direct')}</span>
                <span className="gap-fill__boundary-time">décrochage dans {LIVE_BREAK_LEAD_SEC} s</span>
              </div>

              {openingFits && resumeJingle && (
                <div className="gap-fill__boundary gap-fill__boundary--next">
                  <span className="gap-fill__boundary-role">Départ</span>
                  <span className="gap-fill__boundary-label">{resumeJingle.title}</span>
                  <span className="gap-fill__boundary-time">{fmtSec(openingSec)}</span>
                </div>
              )}

              <LiveFillSelection
                selected={fill.selected}
                elasticSec={elasticSec}
                disabled={editLocked}
                onMove={fill.moveItem}
                onRemove={handleRemoveItem}
              />

              {posesResume && jingleFits && resumeJingle && (
                <div className="gap-fill__boundary gap-fill__boundary--next">
                  <span className="gap-fill__boundary-role">Retour</span>
                  <span className="gap-fill__boundary-label">{resumeJingle.title}</span>
                  <span className="gap-fill__boundary-time">{fmtSec(jingleSec)}</span>
                </div>
              )}

              {posesResume ? (
                <div className="gap-fill__boundary gap-fill__boundary--next">
                  <span className="gap-fill__boundary-role">Reprise</span>
                  <span className="gap-fill__boundary-label">{scheduleItemTitle(liveItem, 'Direct')}</span>
                  <span className="gap-fill__boundary-time">
                    {fmtTime(breakAtMs + effectiveWindowSec * 1000)}
                  </span>
                </div>
              ) : mode === 'OPEN' ? (
                <div className="gap-fill__boundary gap-fill__boundary--next">
                  <span className="gap-fill__boundary-role">Reprise</span>
                  <span className="gap-fill__boundary-label">
                    {resumeJingle
                      ? `À la main, par « Reprendre le direct » — précédée de ${resumeJingle.title}`
                      : 'À la main, par « Reprendre le direct »'}
                  </span>
                  <span className="gap-fill__boundary-time">quand vous voulez</span>
                </div>
              ) : (
                <div className="gap-fill__boundary gap-fill__boundary--end">
                  <span className="gap-fill__boundary-role">Fin</span>
                  <span className="gap-fill__boundary-label">Le bloc de direct s’arrête ici</span>
                  <span className="gap-fill__boundary-time">{fmtTime(blockEndMs)}</span>
                </div>
              )}

              {/* ── Durée du décrochage ── */}
              <div className="live-break__duration">
                <span className="live-break__duration-label" id="live-break-duration">
                  Durée du décrochage
                </span>
                <div className="live-break__presets" role="group" aria-labelledby="live-break-duration">
                  {WINDOW_PRESETS_SEC.map(preset => (
                    <button
                      key={preset}
                      type="button"
                      className="tp-button tp-button--ghost live-break__preset"
                      data-active={(mode === 'FIXED' && windowSec === preset) || undefined}
                      disabled={editLocked || mode === 'OPEN'}
                      onClick={() => { setMode('FIXED'); setWindowSec(preset) }}
                    >
                      {fmtPreset(preset)}
                    </button>
                  ))}
                  <span className="live-break__free" data-disabled={(editLocked || mode === 'OPEN') || undefined}>
                    <input
                      type="number" min={5} max={availableSec || 5} step={1}
                      className="live-break__seconds"
                      value={windowSec}
                      disabled={editLocked || mode === 'OPEN'}
                      onChange={event => setWindowSec(Math.max(5, Number(event.target.value) || 0))}
                      aria-label="Durée du décrochage en secondes"
                    />
                    <span className="live-break__unit">s</span>
                  </span>
                </div>
              </div>

              <label className="live-skip__compact">
                <input
                  type="checkbox" checked={mode === 'OPEN'} disabled={editLocked}
                  onChange={event => setMode(event.target.checked ? 'OPEN' : 'FIXED')}
                />
                <span>
                  Sans durée fixe — je raccroche quand je veux
                  <span className="live-skip__compact-hint">
                    Coché tout seul dès que vous ajoutez « Combler tout le reste » : cet
                    élément va jusqu'au prochain élément posé, il n'a pas de durée à annoncer.
                    L'infoservice tient l'antenne jusqu'à la fin du bloc. Aucune reprise n'est posée
                    d'avance : elle est créée quand vous appuyez sur « Raccrocher ». Si personne ne
                    raccroche, le comblage va jusqu'au programme suivant, sans trou.
                  </span>
                </span>
              </label>

              <label className="live-skip__compact">
                <input
                  type="checkbox" checked={!resume} disabled={editLocked || mode === 'OPEN'}
                  onChange={event => setResume(!event.target.checked)}
                />
                <span>
                  Le direct ne reprend pas — terminer le bloc ici
                  <span className="live-skip__compact-hint">
                    Le comblage va jusqu'au prochain élément déjà posé, et le bloc de direct
                    s'arrête sur ce décrochage.
                  </span>
                </span>
              </label>

              <p className="live-skip__hint">
                Le décrochage part {LIVE_BREAK_LEAD_SEC} s après la confirmation, le temps de
                précharger le premier élément. La fin du bloc et les programmes suivants gardent leur
                heure d'antenne : ils ne bougent jamais.
              </p>
            </div>

            {openingSkipped && resumeJingle && !blockReason && (
              <div className="agenda-editor__notice is-warning" role="status">
                Pas la place du jingle de décrochage ({resumeJingle.title}, {fmtSec(openingDueSec)}) :
                le comblage partira sans.
              </div>
            )}

            {jingleSkipped && resumeJingle && !blockReason && (
              <div className="agenda-editor__notice is-warning" role="status">
                Pas la place du jingle de retour ({resumeJingle.title}, {fmtSec(resumeJingleSec)}) :
                le direct reprendra sans.
              </div>
            )}

            {(error || blockReason) && (
              <div className={`agenda-editor__notice is-${error ? 'danger' : 'warning'}`} role="alert">
                ⚠ {error || blockReason}
              </div>
            )}

            <div className="agenda-editor__actions live-skip__actions">
              <button type="button" className="tp-button tp-button--ghost" onClick={onCancel} disabled={saving}>
                Annuler
              </button>
              <button
                type="button" className="tp-button tp-button--primary"
                onClick={() => { void handleConfirm() }}
                disabled={saving || blockReason !== ''}
              >
                {saving ? 'Décrochage…' : '✓ Décrocher'}
              </button>
            </div>
          </div>

          <LiveFillCatalog
            options={options}
            loading={loading}
            disabled={editLocked}
            hasElastic={fill.hasElastic}
            remainingSec={Math.max(0, effectiveWindowSec - fill.fixedSec - openingSec - jingleSec)}
            onAddSpot={fill.addSpot}
            onAddCampaign={fill.addCampaign}
            onAddInfoservice={fill.addInfoservice}
            onAddInfoserviceRest={handleAddInfoserviceRest}
            onAddProgram={fill.addProgram}
            onAddClip={fill.addClip}
          />
        </div>
      </div>
    </ModalShell>
  )
})
